/**
 * Commerce > Products > detail. Product identity + pricing from the
 * product_catalog view, with a per-warehouse on-hand / committed / available
 * breakdown from inventory_on_hand (GET /v1/commerce/catalog/products/:itemCode).
 * Read-only.
 */

import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import {
  catalogBrandTone,
  formatCatalogMoney,
  formatCatalogQty,
  formatWarehouse,
  hasCommerceProducts,
  labelCatalogBrand,
  useCatalogProduct,
} from '@/lib/useCatalog';

export function CommerceCatalogProductDetail() {
  const { itemCode } = useParams<{ itemCode: string }>();
  const [searchParams] = useSearchParams();
  const backSearch = searchParams.toString();
  const { user } = useSession();
  const canRead = hasCommerceProducts(user?.permissions, user?.superAdmin);

  const productQ = useCatalogProduct(itemCode);
  // The `?from=` param controls where the back link returns:
  //   from=pricing            -> the Connect pricing manager
  //   from=order&order={id}   -> the originating order detail page
  //   (absent)                -> the products grid, preserving its filters
  const from = searchParams.get('from');
  const fromPricing = from === 'pricing';
  const fromOrderId = from === 'order' ? searchParams.get('order') : null;

  let backHref: { pathname: string; search: string };
  let backLabel: string;
  if (fromPricing) {
    backHref = { pathname: '/connect/pricing', search: '' };
    backLabel = 'Back to pricing';
  } else if (fromOrderId) {
    backHref = { pathname: `/commerce/orders/${encodeURIComponent(fromOrderId)}`, search: '' };
    backLabel = 'Back to order';
  } else {
    backHref = { pathname: '/commerce/products', search: backSearch ? `?${backSearch}` : '' };
    backLabel = 'Back to products';
  }

  const backLink = (
    <Link
      to={backHref}
      className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
    >
      <ArrowLeft className="h-3 w-3" /> {backLabel}
    </Link>
  );

  if (user && !canRead) {
    return (
      <div className="pb-4">
        {backLink}
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">No access.</p>
      </div>
    );
  }

  const product = productQ.data;

  return (
    <div className="pb-4">
      {backLink}

      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Commerce / Products</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink font-mono">
          {product?.item_code ?? itemCode}
        </h1>
        {product?.description && (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{product.description}</p>
        )}
      </div>

      {productQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : productQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {productQ.error.message}</p>
      ) : !product ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Product not found.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Identity">
              <Row label="Item code"><span className="font-mono text-xs">{product.item_code}</span></Row>
              <Row label="Description">{product.description ?? '--'}</Row>
              <Row label="Brand"><Pill tone={catalogBrandTone(product.brand)}>{labelCatalogBrand(product.brand)}</Pill></Row>
              <Row label="Active">{product.active ? 'Yes' : 'No'}</Row>
            </Card>
            <Card title="Pricing & inventory">
              <Row label="UOM">{product.uom ?? '--'}</Row>
              <Row label="Case pack">{product.case_pack ?? '--'}</Row>
              <Row label="Price">{formatCatalogMoney(product.price)}</Row>
              <Row label="Total on hand">{formatCatalogQty(product.on_hand)}</Row>
            </Card>
          </div>

          <div className="border border-amp-line rounded-lg bg-amp-white overflow-hidden dark:border-amp-dark-line dark:bg-amp-dark-surface">
            <div className="border-b border-amp-line px-4 py-2 dark:border-amp-dark-line">
              <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
                On hand by warehouse <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted font-normal">({product.inventory.length})</span>
              </h2>
            </div>
            {product.inventory.length === 0 ? (
              <p className="px-4 py-4 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No warehouse inventory on file.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Brand</th>
                    <th className="px-4 py-2 text-left font-medium">Warehouse</th>
                    <th className="px-4 py-2 text-right font-medium w-[120px]">On Hand</th>
                    <th className="px-4 py-2 text-right font-medium w-[120px]">Committed</th>
                    <th className="px-4 py-2 text-right font-medium w-[120px]">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
                  {product.inventory.map((inv, i) => (
                    <tr key={`${inv.brand}:${inv.warehouse}:${i}`} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                      <td className="px-4 py-2"><Pill tone={catalogBrandTone(inv.brand)}>{labelCatalogBrand(inv.brand)}</Pill></td>
                      <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{formatWarehouse(inv.warehouse, inv.warehouse_name)}</td>
                      <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(inv.on_hand)}</td>
                      <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(inv.committed)}</td>
                      <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCatalogQty(inv.available)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-2">{title}</h2>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-sm py-0.5">
      <span className="w-32 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</span>
      <span className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">{children}</span>
    </div>
  );
}
